/**
 * Real-time Whale Activity Monitor
 *
 * Subscribes to PumpPortal WebSocket for tracked whale wallets
 * and triggers notifications when they make trades.
 */

import { useEffect, useRef, useCallback } from 'react';
import { pumpPortal } from '../lib/websocket';
import type { PumpEvent, PumpTrade } from '../lib/websocket';
import {
  getTrackedWallets,
  addWhaleAlert,
  type TrackedWallet,
  type WalletActivity,
} from '../lib/whales';

interface UseWhaleMonitorOptions {
  userWallet: string | null;
  enabled?: boolean;
  onWhaleActivity?: (wallet: TrackedWallet, activity: WalletActivity) => void;
}

export function useWhaleMonitor({
  userWallet,
  enabled = true,
  onWhaleActivity,
}: UseWhaleMonitorOptions) {
  const walletsRef = useRef<TrackedWallet[]>([]);
  const walletMapRef = useRef<Map<string, TrackedWallet>>(new Map());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const notifiedTxsRef = useRef<Set<string>>(new Set());

  // Update tracked wallets list
  const refreshWallets = useCallback(() => {
    if (!userWallet) {
      walletsRef.current = [];
      walletMapRef.current.clear();
      return [];
    }
    const wallets = getTrackedWallets(userWallet);
    walletsRef.current = wallets;

    // Build lookup map
    walletMapRef.current.clear();
    for (const w of wallets) {
      walletMapRef.current.set(w.address, w);
    }

    return wallets;
  }, [userWallet]);

  // Handle incoming trade events from tracked wallets
  const handleTrade = useCallback((event: PumpEvent) => {
    if (event.txType !== 'buy' && event.txType !== 'sell') return;
    if (!userWallet) return;

    const trade = event as PumpTrade;
    const traderAddress = trade.traderPublicKey;

    // Check if this trader is a tracked wallet
    const trackedWallet = walletMapRef.current.get(traderAddress);
    if (!trackedWallet) return;

    // Avoid duplicate notifications for same transaction
    if (notifiedTxsRef.current.has(trade.signature)) return;
    notifiedTxsRef.current.add(trade.signature);

    // Limit set size to prevent memory issues
    if (notifiedTxsRef.current.size > 1000) {
      const arr = [...notifiedTxsRef.current];
      notifiedTxsRef.current = new Set(arr.slice(-500));
    }

    // Create activity record
    const activity: WalletActivity = {
      id: trade.signature,
      walletAddress: traderAddress,
      type: trade.txType,
      tokenAddress: trade.mint,
      tokenSymbol: 'UNKNOWN', // PumpPortal doesn't include symbol
      tokenName: 'Unknown Token',
      amount: trade.tokenAmount,
      amountUsd: (trade.solAmount / 1e9) * 150, // Rough USD estimate
      pricePerToken: trade.vSolInBondingCurve / trade.vTokensInBondingCurve / 1e9,
      timestamp: Date.now(),
      signature: trade.signature,
    };

    // Save whale alert
    addWhaleAlert(userWallet, traderAddress, trackedWallet.label, activity);

    // Send browser notification
    sendWhaleNotification(trackedWallet, activity);

    // Callback for UI updates
    onWhaleActivity?.(trackedWallet, activity);

    console.log('[WhaleMonitor] Whale activity:', trackedWallet.label, trade.txType, trade.mint);
  }, [userWallet, onWhaleActivity]);

  // Subscribe to account trades for tracked wallets
  const subscribe = useCallback(async () => {
    const wallets = refreshWallets();
    if (wallets.length === 0) return;

    const walletAddresses = wallets.map(w => w.address);

    try {
      await pumpPortal.connect();
      unsubscribeRef.current = pumpPortal.subscribeAccountTrades(walletAddresses, handleTrade);
      console.log('[WhaleMonitor] Subscribed to', walletAddresses.length, 'wallets');
    } catch (error) {
      console.error('[WhaleMonitor] Subscribe error:', error);
    }
  }, [refreshWallets, handleTrade]);

  // Resubscribe when wallets change
  const resubscribe = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    subscribe();
  }, [subscribe]);

  useEffect(() => {
    if (!enabled || !userWallet) {
      return;
    }

    subscribe();

    // Refresh wallets periodically in case user adds new ones
    const interval = setInterval(() => {
      const newWallets = refreshWallets();
      const currentAddresses = new Set(walletsRef.current.map(w => w.address));
      const newAddresses = new Set(newWallets.map(w => w.address));

      // Check if wallet list changed
      const changed = newAddresses.size !== currentAddresses.size ||
        [...newAddresses].some(a => !currentAddresses.has(a));

      if (changed) {
        console.log('[WhaleMonitor] Tracked wallets changed, resubscribing...');
        resubscribe();
      }
    }, 10000); // Check every 10 seconds

    return () => {
      clearInterval(interval);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [enabled, userWallet, subscribe, refreshWallets, resubscribe]);

  return {
    refreshWallets,
    resubscribe,
  };
}

/**
 * Send browser notification for whale activity
 */
function sendWhaleNotification(wallet: TrackedWallet, activity: WalletActivity): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  const action = activity.type === 'buy' ? 'bought' : 'sold';
  const solAmount = activity.amountUsd ? `($${activity.amountUsd.toFixed(2)})` : '';

  const title = `${wallet.label} ${action}!`;
  const body = `${activity.type === 'buy' ? 'Bought' : 'Sold'} ${activity.tokenSymbol} ${solAmount}`;

  const notification = new Notification(title, {
    body,
    icon: '/favicon.ico',
    tag: `whale_${activity.signature}`,
    requireInteraction: false,
  });

  notification.onclick = () => {
    window.focus();
    window.location.href = `/${activity.tokenAddress}`;
    notification.close();
  };

  // Auto-close after 10 seconds
  setTimeout(() => notification.close(), 10000);
}
